'use client';

import { Button, fieldBaseClass, FieldDescription, FieldError, FieldLabel, RenderCustomComponent } from '@payloadcms/ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RgbaColor, RgbaColorPicker } from 'react-colorful';

import type { ChangeEvent } from 'react';
import type { ColorPickerInputProps } from './types.js';

import './index.scss';

import { rgbaToText, textToRgba } from '@/utils/colors';

const baseClass = 'color';

export const ColorPickerInput: React.FC<ColorPickerInputProps> = (props) => {
  const {
    AfterInput,
    BeforeInput,
    className,
    colors,
    Description,
    description,
    Error,
    inputRef,
    Label,
    label,
    localized,
    onChange,
    onKeyDown,
    path,
    placeholder,
    readOnly,
    required,
    rtl,
    showError,
    style,
    value,
  } = props;

  const [fieldIsFocused, setFieldIsFocused] = useState(false);
  const lastValueRef = useRef(value);

  // Avoid react-colorful's stale-effect infinite loop by never mutating
  // the color prop after mount. Re-mount via key for external changes only.
  const pickerColor = useRef<RgbaColor | undefined>(textToRgba(value));
  const [pickerKey, setPickerKey] = useState(0);
  const isFromPickerRef = useRef(false);

  useEffect(() => {
    if (isFromPickerRef.current) {
      isFromPickerRef.current = false;
      return;
    }
    const parsed = textToRgba(value);
    if (parsed) {
      pickerColor.current = parsed;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPickerKey((k) => k + 1);
    }
  }, [value]);

  const handleColorPickerChange = useCallback(
    (v: RgbaColor) => {
      isFromPickerRef.current = true;
      onChange?.({
        target: {
          name: path,
          value: rgbaToText(v),
        },
      } as React.ChangeEvent<HTMLInputElement>);
    },
    [path, onChange],
  );

  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    if (!evt.target.value.startsWith('#')) {
      evt.target.value = `#${evt.target.value}`;
    }

    evt.target.value = evt.target.value.replace(/[^a-f0-9#]/gi, '').slice(0, 7);

    if (lastValueRef.current !== evt.target.value) {
      lastValueRef.current = evt.target.value;
      onChange?.(evt as React.ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <div
      className={[fieldBaseClass, 'color', className, showError && 'error', readOnly && 'read-only'].filter(Boolean).join(' ')}
      style={style}
    >
      <RenderCustomComponent
        CustomComponent={Label}
        Fallback={<FieldLabel label={label} localized={localized} path={path} required={required} />}
      />
      <div className={`${fieldBaseClass}__wrap`}>
        <RenderCustomComponent CustomComponent={Error} Fallback={<FieldError path={path} showError={showError} />} />
        {BeforeInput}
        <div
          className={`${baseClass}__input-container`}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setFieldIsFocused(false);
            }
          }}
          onFocus={() => setFieldIsFocused(true)}
        >
          <div
            className={`${baseClass}__color-preview`}
            style={{
              background: value?.length && value?.length > 1 ? value : '#fff',
            }}
          />
          <input
            data-rtl={rtl}
            disabled={readOnly}
            id={`field-${path.replace(/\./g, '__')}`}
            name={path}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder as string}
            ref={inputRef}
            type="text"
            value={value || ''}
          />
          <div
            className={`
            ${baseClass}__color-picker-modal 
            ${rtl ? `${baseClass}__color-picker-modal--rtl` : ''}
            ${fieldIsFocused ? `${baseClass}__color-picker-modal--focused` : ''}
          `}
          >
            {colors && (
              <div className={`${baseClass}__color-picker-modal__predefined-colors`}>
                {colors.map((color, index) => (
                  <Button
                    buttonStyle="none"
                    className={`${baseClass}__color-picker-modal__button`}
                    key={index}
                    onClick={() => {
                      if (color !== value) {
                        onChange?.({
                          target: {
                            name: path,
                            value: color,
                          },
                        } as React.ChangeEvent<HTMLInputElement>);
                      }
                    }}
                  >
                    <span
                      className={`${baseClass}__color-picker-modal__button__color`}
                      style={{
                        background: color,
                      }}
                    />
                  </Button>
                ))}
              </div>
            )}
            {/* eslint-disable-next-line react-hooks/refs */}
            <RgbaColorPicker key={pickerKey} color={pickerColor.current} onChange={handleColorPickerChange} />
          </div>
        </div>
        {AfterInput}
        <RenderCustomComponent CustomComponent={Description} Fallback={<FieldDescription description={description} path={path} />} />
      </div>
    </div>
  );
};
